import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { getSocket, useSocketStore } from '../../lib/socket';
import { SocketEvents, decryptChannelMessage, decryptDMAuto } from '@clinikchat/shared';
import { useAuthStore } from '../../stores/authStore';
import { useCryptoStore } from '../../stores/cryptoStore';
import MessageItem from './MessageItem';
import { withFileMeta } from '../file/AttachmentView';
import { useEpochBackfill } from '../../hooks/useEpochBackfill';

interface AttachmentData {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;
  thumbnailUrl?: string | null;
}

interface MessageData {
  id: string;
  channelId: string;
  userId: string;
  content: string;
  encrypted?: boolean;
  /**
   * Channel-key epoch this message was encrypted with, or null for
   * pre-epoch legacy rows. Decryption picks the right key from
   * epochKeyRing when set; falls back to the legacy channel key when
   * null (see @clinikchat/shared decryptChannelMessage).
   */
  keyEpoch?: number | null;
  type: string;
  parentId: string | null;
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    email?: string;
    status?: string;
  };
  attachments?: AttachmentData[];
}

interface MessagesPage {
  items: MessageData[];
  hasMore: boolean;
  nextCursor: string | null;
  prevCursor: string | null;
}

interface ListItem {
  type: 'message' | 'date-separator';
  data: MessageData | string;
  id: string;
  isGrouped?: boolean;
}

function buildListItems(messages: MessageData[]): ListItem[] {
  const items: ListItem[] = [];
  let lastDate = '';
  let lastUserId = '';

  for (const msg of messages) {
    const date = new Date(msg.createdAt).toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

    if (date !== lastDate) {
      items.push({ type: 'date-separator', data: date, id: `sep-${date}` });
      lastDate = date;
      lastUserId = '';
    }

    const grouped = items.length > 0 && lastUserId === msg.userId;
    items.push({ type: 'message', data: msg, id: msg.id, isGrouped: grouped });
    lastUserId = msg.userId;
  }

  return items;
}

interface Props {
  channelId: string;
  channelType: string; // 'PRIVATE' | 'DM'
  /**
   * Identity-stable signal that the epoch key ring has been populated for
   * this channel. The ring is a module-scoped singleton mutated by
   * `useChannelEpochKey`; React is otherwise blind to those writes, so the
   * decrypt `useMemo` below depends on this prop to bust its cache once a
   * key arrives. The Uint8Array reference itself is the dependency value —
   * we never read its contents here; `decryptChannelMessage` looks up the
   * correct epoch key directly from the ring.
   */
  epochKeySignal?: Uint8Array | null;
  onEditStart?: (messageId: string, content: string) => void;
  onImageClick?: (url: string, index: number) => void;
  onUserClick?: (
    user: NonNullable<MessageData['user']>,
    position: { x: number; y: number },
  ) => void;
  /** userId → displayName for reaction tooltips; from the channel member list. */
  memberNames?: Record<string, string>;
}

type QueryData = { pages: MessagesPage[]; pageParams: (string | undefined)[] };

function decryptContent(
  message: { content: string; encrypted?: boolean; keyEpoch?: number | null; channelId?: string },
  channelId: string,
  channelType: string,
  channelKey: string | null,
  secretKey: string | null,
): string {
  if (!message.encrypted) return message.content;

  if (!secretKey) {
    return '[Log in again to decrypt messages]';
  }

  if (channelType === 'DM') {
    try {
      const dmContent = JSON.parse(message.content) as { forRecipient: string; forSender: string };
      return decryptDMAuto(dmContent, secretKey) ?? '[Message could not be decrypted]';
    } catch {
      return '[Encrypted message]';
    }
  }

  const decrypted = decryptChannelMessage(message.content, channelId, message.keyEpoch, channelKey);
  if (decrypted !== null) return decrypted;

  // No key material at all — ring empty and legacy key absent.
  if (!channelKey && (message.keyEpoch === null || message.keyEpoch === undefined)) {
    return '[Encryption key unavailable]';
  }
  return '[Message could not be decrypted]';
}

export default function MessageList({
  channelId,
  channelType,
  epochKeySignal,
  onEditStart,
  onImageClick,
  onUserClick,
  memberNames,
}: Props) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const secretKey = useCryptoStore((s) => s.secretKey);
  const channelKey = useCryptoStore((s) => s.channelKeys[channelId]);
  // Re-run the socket-listener effect when the socket is replaced (see socketId in socket.ts).
  const socketId = useSocketStore((s) => s.socketId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  // Used to preserve scroll position when older messages are prepended
  const prevScrollHeightRef = useRef<number>(0);
  const isLoadingMoreRef = useRef(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    MessagesPage,
    Error,
    { pages: MessagesPage[]; pageParams: (string | undefined)[] },
    string[],
    string | undefined
  >({
    queryKey: ['messages', channelId],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) {
        params.set('cursor', pageParam);
        params.set('direction', 'before');
      }
      params.set('limit', '50');
      const { data } = await api.get(`/api/v1/channels/${channelId}/messages?${params}`);
      return data.data as MessagesPage;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.prevCursor ?? undefined) : undefined,
    enabled: !!channelId,
  });

  // Memoize decryption + list building so they only re-run when the underlying
  // message pages actually change. Previously this re-ran on every render —
  // including renders triggered by scroll/hover state — which dominates CPU on
  // busy channels with hundreds of messages.
  // Epochs the loaded messages and their attachments were encrypted under. The
  // device only syncs the current epoch on its own; the rest are fetched here so
  // history (shared with a new member, or from before a rotation) can decrypt.
  const referencedEpochs = useMemo(() => {
    const found = new Set<number>();
    for (const page of data?.pages ?? []) {
      for (const m of page.items) {
        if (typeof m.keyEpoch === 'number') found.add(m.keyEpoch);
        for (const a of (m.attachments ?? []) as { keyEpoch?: number | null }[]) {
          if (typeof a.keyEpoch === 'number') found.add(a.keyEpoch);
        }
      }
    }
    return [...found].sort((a, b) => a - b);
  }, [data?.pages]);
  const backfillVersion = useEpochBackfill(channelId, referencedEpochs);

  const allMessages = useMemo<MessageData[]>(() => {
    if (!data?.pages) return [];
    // Each page is ascending (oldest → newest) and every "load more" page is
    // OLDER than the last, appended by fetchNextPage: pages = [newest, older,
    // oldest…]. Flatten oldest-page-first so the timeline stays chronological
    // Socket appends still target pages[0], which remains the newest page.
    return [...data.pages]
      .reverse()
      .flatMap((p) => p.items)
      .map((msg) =>
        msg.type === 'FILE'
          ? // A FILE message's content is metadata JSON, not an encrypted text
            // body. AttachmentView decrypts the filename/MIME from it on demand.
            withFileMeta(msg)
          : {
              ...msg,
              content: decryptContent(msg, channelId, channelType, channelKey ?? null, secretKey),
            },
      );
    // `epochKeySignal` is intentionally part of the dep list even though it
    // isn't read in the body: it busts the memo cache once the epoch ring
    // has been populated by `useChannelEpochKey`, which `decryptContent` →
    // `decryptChannelMessage` reads from internally. Without it the first
    // render (ring empty) latches "[Message could not be decrypted]" forever.
  }, [data?.pages, channelId, channelType, channelKey, secretKey, epochKeySignal, backfillVersion]);

  const items = useMemo(() => buildListItems(allMessages), [allMessages]);

  // Optimistic local delete — also broadcast via socket for other users
  const handleOptimisticDelete = useCallback(
    (messageId: string) => {
      queryClient.setQueryData<QueryData>(['messages', channelId], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((m) => m.id !== messageId),
          })),
        };
      });
    },
    [queryClient, channelId],
  );

  // Socket event handlers
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Re-fetch on reconnect to catch missed deletes/edits on mobile
    function handleReconnect() {
      queryClient.invalidateQueries({ queryKey: ['messages', channelId] });
    }
    socket.on('connect', handleReconnect);

    function handleNewMessage(message: MessageData) {
      if (message.channelId !== channelId) return;

      // Cache the message EXACTLY as it arrived — ciphertext included.
      // Decryption happens once, at render, in the allMessages memo. Decrypting
      // here too overwrote the cached `content` with the decrypted string (or a
      // placeholder) while leaving `encrypted: true`, so the render pass then
      // tried to decrypt already-plain text, failed, and showed
      // "[Message could not be decrypted]" until a refetch restored the real
      // ciphertext — which is why refreshing appeared to fix it.
      queryClient.setQueryData<QueryData>(['messages', channelId], (old) => {
        if (!old) return old;
        const newPages = [...old.pages];
        const firstPage = { ...newPages[0] };
        firstPage.items = [...firstPage.items, message];
        newPages[0] = firstPage;
        return { ...old, pages: newPages };
      });

      if (message.userId === currentUser?.id || autoScroll) {
        setAutoScroll(true);
        setHasNewMessage(false);
      } else {
        setHasNewMessage(true);
      }
    }

    function handleEditMessage(message: MessageData) {
      if (message.channelId !== channelId) return;
      // Same as handleNewMessage — cache raw ciphertext, decrypt once at render.
      queryClient.setQueryData<QueryData>(['messages', channelId], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((m) => (m.id === message.id ? message : m)),
          })),
        };
      });
    }

    function handleDeleteMessage(evtData: { messageId: string; channelId: string }) {
      if (evtData.channelId !== channelId) return;
      queryClient.setQueryData<QueryData>(['messages', channelId], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((m) => m.id !== evtData.messageId),
          })),
        };
      });
    }

    function applyReactionChange(
      evtData: { messageId: string; userId: string; emoji: string },
      kind: 'add' | 'remove',
    ) {
      // Targeted cache update — previously this invalidated the entire
      // infinite query which forced a full refetch of every message page
      // just to learn about a single reaction toggle. Now we mutate the
      // affected message in-place so there is no network round-trip.
      queryClient.setQueryData<QueryData>(['messages', channelId], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((m) => {
              if (m.id !== evtData.messageId) return m;
              const current =
                (
                  m as MessageData & {
                    reactions?: { emoji: string; count: number; userIds: string[] }[];
                  }
                ).reactions ?? [];
              const next = current.map((r) => ({ ...r, userIds: [...r.userIds] }));
              const idx = next.findIndex((r) => r.emoji === evtData.emoji);
              if (kind === 'add') {
                if (idx >= 0) {
                  if (!next[idx].userIds.includes(evtData.userId)) {
                    next[idx].userIds.push(evtData.userId);
                    next[idx].count = next[idx].userIds.length;
                  }
                } else {
                  next.push({ emoji: evtData.emoji, count: 1, userIds: [evtData.userId] });
                }
              } else if (idx >= 0) {
                next[idx].userIds = next[idx].userIds.filter((u) => u !== evtData.userId);
                next[idx].count = next[idx].userIds.length;
                if (next[idx].count === 0) next.splice(idx, 1);
              }
              return { ...m, reactions: next } as MessageData;
            }),
          })),
        };
      });
    }

    function handleReactionAdd(evtData: { messageId: string; userId: string; emoji: string }) {
      applyReactionChange(evtData, 'add');
    }
    function handleReactionRemove(evtData: { messageId: string; userId: string; emoji: string }) {
      applyReactionChange(evtData, 'remove');
    }

    socket.on(SocketEvents.MESSAGE_NEW, handleNewMessage);
    socket.on(SocketEvents.MESSAGE_EDIT, handleEditMessage);
    socket.on(SocketEvents.MESSAGE_DELETE, handleDeleteMessage);
    socket.on(SocketEvents.REACTION_ADD, handleReactionAdd);
    socket.on(SocketEvents.REACTION_REMOVE, handleReactionRemove);

    return () => {
      socket.off('connect', handleReconnect);
      socket.off(SocketEvents.MESSAGE_NEW, handleNewMessage);
      socket.off(SocketEvents.MESSAGE_EDIT, handleEditMessage);
      socket.off(SocketEvents.MESSAGE_DELETE, handleDeleteMessage);
      socket.off(SocketEvents.REACTION_ADD, handleReactionAdd);
      socket.off(SocketEvents.REACTION_REMOVE, handleReactionRemove);
    };
  }, [
    channelId,
    queryClient,
    autoScroll,
    currentUser?.id,
    channelType,
    channelKey,
    secretKey,
    socketId,
  ]);

  // Auto-scroll to bottom when new messages arrive and user is at bottom
  useEffect(() => {
    if (autoScroll && items.length > 0) {
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
  }, [items.length, autoScroll]);

  // Preserve scroll position when older messages are prepended
  useEffect(() => {
    if (isLoadingMoreRef.current && !isFetchingNextPage) {
      isLoadingMoreRef.current = false;
      const el = scrollRef.current;
      if (el) {
        const newScrollHeight = el.scrollHeight;
        el.scrollTop = newScrollHeight - prevScrollHeightRef.current;
      }
    }
  }, [isFetchingNextPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isAtBottom = distanceFromBottom < 100;
    setAutoScroll(isAtBottom);
    if (isAtBottom) setHasNewMessage(false);

    if (el.scrollTop < 200 && hasNextPage && !isFetchingNextPage && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  function scrollToBottom() {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAutoScroll(true);
    setHasNewMessage(false);
  }

  if (allMessages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-1 text-warm-gray-400 dark:text-warm-gray-500">
        {/*
         * Spot illustration — two layered speech bubbles.
         * Flat 2D geometric style; uses `currentColor` (text-primary) so
         * it adapts to both light and dark mode without separate variants.
         * fillOpacity/strokeOpacity layers build depth without gradients.
         */}
        <svg
          className="mb-3 h-16 w-16 text-primary"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Back speech bubble — softer, lower-left */}
          <rect
            x="2"
            y="20"
            width="38"
            height="26"
            rx="8"
            fill="currentColor"
            fillOpacity="0.08"
            stroke="currentColor"
            strokeOpacity="0.12"
            strokeWidth="1.5"
          />
          {/* Back bubble typing dots */}
          <circle cx="12" cy="33" r="2" fill="currentColor" fillOpacity="0.28" />
          <circle cx="21" cy="33" r="2" fill="currentColor" fillOpacity="0.28" />
          <circle cx="30" cy="33" r="2" fill="currentColor" fillOpacity="0.28" />
          {/* Back bubble tail */}
          <path d="M10 46 Q6 53 4 57 Q12 54 16 49Z" fill="currentColor" fillOpacity="0.08" />

          {/* Front speech bubble — stronger, upper-right */}
          <rect
            x="24"
            y="6"
            width="38"
            height="26"
            rx="8"
            fill="currentColor"
            fillOpacity="0.14"
            stroke="currentColor"
            strokeOpacity="0.22"
            strokeWidth="1.5"
          />
          {/* Front bubble typing dots — more opaque to establish foreground */}
          <circle cx="34" cy="19" r="2.5" fill="currentColor" fillOpacity="0.55" />
          <circle cx="43" cy="19" r="2.5" fill="currentColor" fillOpacity="0.55" />
          <circle cx="52" cy="19" r="2.5" fill="currentColor" fillOpacity="0.55" />
          {/* Front bubble tail */}
          <path d="M54 32 Q58 39 60 43 Q52 40 48 35Z" fill="currentColor" fillOpacity="0.14" />
        </svg>
        <p className="text-h3 font-medium text-warm-gray-600 dark:text-warm-gray-400">
          No messages yet
        </p>
        <p className="text-body-sm text-warm-gray-400 dark:text-warm-gray-500">
          Start the conversation!
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="relative flex-1 overflow-y-auto">
      {hasNewMessage && (
        <button
          onClick={scrollToBottom}
          className="sticky top-2 z-10 mx-auto flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-body-sm text-white shadow-lg hover:bg-primary-hover"
          style={{ display: 'block', left: '50%', transform: 'translateX(-50%)' }}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
          New messages
        </button>
      )}

      {isFetchingNextPage && (
        <div className="flex justify-center py-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      <div className="mx-auto flex w-full max-w-[860px] flex-col pb-2">
        {items.map((item) => {
          if (item.type === 'date-separator') {
            return (
              <div key={item.id} className="flex items-center px-6 py-2">
                <div className="flex-1 border-t border-warm-border-light dark:border-warm-dark-border" />
                <span className="mx-3 text-caption font-medium text-warm-gray-500 dark:text-warm-gray-500">
                  {item.data as string}
                </span>
                <div className="flex-1 border-t border-warm-border-light dark:border-warm-dark-border" />
              </div>
            );
          }

          const msg = item.data as MessageData;
          return (
            <MessageItem
              key={item.id}
              message={msg}
              isGrouped={item.isGrouped || false}
              onEditStart={onEditStart}
              onDelete={handleOptimisticDelete}
              onImageClick={onImageClick}
              onUserClick={onUserClick}
              memberNames={memberNames}
            />
          );
        })}
      </div>
    </div>
  );
}
